import { useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router";

import {
  type CheckoutSession,
  CheckoutStatus,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
} from "@/types";
import { defaultEventAttributes } from "@/events";
import { withCheckoutId } from "@/lib/checkout-id";

import { isEarlyRepaymentRouteActive } from "@/modules/early-repayment/lib/repayment-flow";
import { useCheckoutSessionV3 } from "../api-hooks/use-checkout-session";

/**
 * Maps a checkout status to the route path it should navigate to.
 * Returns empty string when no redirect is needed.
 */
export function getRouteForStatus(status: CheckoutStatus): string {
  if (status === CheckoutStatus.AWAIT_IVR_CONFIRMATION) {
    return "/ivr";
  }

  if (
    status === CheckoutStatus.AWAIT_EARLY_REPYAYMENT_COMPLETION ||
    status === CheckoutStatus.EARLY_REPAYMENT_COMPLETED
  ) {
    return "/early-repayment";
  }

  if (
    CHECKOUT_STATUS_PROCESSING_PAYMENT.concat(
      CheckoutStatus.AWAIT_PAYMENT_METHOD_SELECTION,
    ).includes(status)
  ) {
    return "/confirmation";
  }

  if (
    [CheckoutStatus.FINISHED, CheckoutStatus.SUCCEEDED].includes(status)
  ) {
    return "/success";
  }

  if (status === CheckoutStatus.AWAIT_QUESTIONNAIRE_COMPLETION) {
    return "/questionnaire";
  }

  if (status === CheckoutStatus.AWAIT_IDV_COMPLETION) {
    return "/kyc";
  }

  if (
    [
      CheckoutStatus.AWAIT_PAYMENT_OPTION_SELECTION,
      CheckoutStatus.EVALUATING_ORDER_CREDIT,
      CheckoutStatus.CHECKING_QUESTIONNAIRE_REQUIRED,
      // Direct successor of CHECKING_QUESTIONNAIRE_REQUIRED — still part of
      // credit evaluation, which only polls on the plan screen.
      CheckoutStatus.CHECKING_OVERDUE_PAYMENTS,
      CheckoutStatus.CHECKING_IDV_ELIGIBILITY,
      CheckoutStatus.VERIFYING_IDV_RESULT,
      // Questionnaire counterpart of VERIFYING_IDV_RESULT.
      CheckoutStatus.VERIFYING_QUESTIONNAIRE_RESULT,
    ].includes(status)
  ) {
    return "/";
  }

  return "";
}

interface SessionRef {
  sessionId: string;
  status: CheckoutStatus;
}

/**
 * Resolves the SPA path from the full session.
 *
 * `earlyRepayment` / `overduePayments` stay populated on the session even
 * AFTER repayment completes (backend never nulls them out), so we cannot gate
 * on object presence alone — we must also check status.
 *
 * Three cases route back to /early-repayment:
 *   1. AWAIT_EARLY_REPYAYMENT_COMPLETION — still waiting for user to pay
 *   2. EARLY_REPAYMENT_COMPLETED        — payment done, showing result screen
 *   3. A processing status + repayment payload present — payment in flight
 *      (e.g. 3DS redirect returned to /instalment/add-payment-method/success
 *       while the backend is still processing the card payment)
 *
 * Once status reaches AWAIT_PAYMENT_OPTION_SELECTION the ERC is done and we
 * fall through to getRouteForStatus → "/" so the normal checkout resumes.
 */
export function getRouteForSession(session: CheckoutSession): string {
  const ercActive =
    isEarlyRepaymentRouteActive(session) &&
    (session.status === CheckoutStatus.AWAIT_EARLY_REPYAYMENT_COMPLETION ||
      session.status === CheckoutStatus.EARLY_REPAYMENT_COMPLETED ||
      CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(session.status));

  if (ercActive) return "/early-repayment";
  return getRouteForStatus(session.status);
}

/**
 * Hook that handles session-driven navigation.
 *
 * Behavioral contract (must not change):
 * - Updates SWR cache with fresh session (no revalidation)
 * - Dispatches "checkout_default_event_attributes" CustomEvent
 * - Logs session changes only when sessionId or status changes
 * - Never navigates away from /3ds, /success, or /early-repayment
 * - Uses replace navigation to prevent back-button loops
 */
export function useCheckoutRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate } = useCheckoutSessionV3();
  const lastLoggedSessionRef = useRef<SessionRef | null>(null);

  const handleCheckoutFetched = useCallback(
    (session: CheckoutSession) => {
      const targetRoute = getRouteForSession(session);

      const lastLogged = lastLoggedSessionRef.current;
      const hasChanged =
        !lastLogged ||
        lastLogged.sessionId !== session.id ||
        lastLogged.status !== session.status;

      mutate(session, { revalidate: false });

      window.dispatchEvent(
        new CustomEvent("checkout_default_event_attributes", {
          detail: defaultEventAttributes(session),
        }),
      );

      if (hasChanged) {
        lastLoggedSessionRef.current = {
          sessionId: session.id,
          status: session.status,
        };
      }

      const selfNavigatingPaths = [
        "/3ds",
        "/questionnaire",
        "/questionnaire/return",
        "/kyc",
        "/kyc/return",
        "/success",
        "/early-repayment",
      ];
      if (selfNavigatingPaths.includes(location.pathname)) {
        return;
      }

      // /early-repayment self-manages while submission/polling is in flight.
      // Once the BE moves the status out of the processing-payment window
      // (e.g. back to AWAIT_PAYMENT_OPTION_SELECTION), normal navigation
      // resumes via the standard mapping above.
      if (
        location.pathname === "/early-repayment" &&
        CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(session.status)
      ) {
        return;
      }

      if (targetRoute && targetRoute !== location.pathname) {
        navigate(withCheckoutId(targetRoute), { replace: true });
      }
    },
    [navigate, mutate, location.pathname],
  );

  return { handleCheckoutFetched };
}
